import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ClientCredentialsBlock from '../components/ClientCredentialsBlock';
import { FormPageLayout } from '../components/layout/FormPageLayout';
import { useTransitionNavigate } from '../hooks/useTransitionNavigate';
import { clearBookingFormDraft } from '../lib/form-return';
import { getApiErrorMessage } from '../lib/api-error';
import { useInviteClientMutation } from '../store/coachApi';

const InviteClientRoute = () => {
  const { t } = useTranslation();
  const navigate = useTransitionNavigate();
  const [inviteClient, { isLoading }] = useInviteClientMutation();
  const [inviteLogin, setInviteLogin] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [error, setError] = useState('');
  const [credentials, setCredentials] = useState<{
    login: string;
    password: string;
  } | null>(null);
  const [createdClientId, setCreatedClientId] = useState<string | null>(null);

  const close = () => navigate(-1);
  const openCreatedClient = (clientId: string) => {
    navigate(`/clients/${clientId}`, { replace: true });
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const result = await inviteClient({
        login: inviteLogin,
        name: inviteName,
        email: inviteEmail || undefined,
      }).unwrap();
      if (result.client.login && result.password) {
        setCreatedClientId(result.client.id);
        setCredentials({
          login: result.client.login,
          password: result.password,
        });
      } else {
        openCreatedClient(result.client.id);
      }
    } catch (err) {
      if (
        err &&
        typeof err === 'object' &&
        'status' in err &&
        err.status === 409
      ) {
        setError(t('Такой пользователь уже существует'));
      } else if (
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof err.message === 'string'
      ) {
        setError(err.message);
      } else {
        setError(getApiErrorMessage(err, 'Не удалось создать клиента'));
      }
    }
  };

  if (credentials) {
    return (
      <FormPageLayout
        title={t('Клиент создан')}
        onBack={() => (createdClientId ? openCreatedClient(createdClientId) : close())}
        maxWidthClassName="max-w-md"
      >
        <p className="text-sm text-slate-600 mb-4">
          {t(
            'Отправьте клиенту логин и пароль — он сможет войти в мобильное приложение.',
          )}
        </p>

        <ClientCredentialsBlock
          login={credentials.login}
          password={credentials.password}
        />

        {createdClientId && (
          <div className="mt-6 space-y-3">
            <p className="text-sm font-semibold text-slate-800">
              {t('Клиент готов — что дальше?')}
            </p>
            <button
              type="button"
              className="btn-primary w-full min-h-12"
              onClick={() => {
                clearBookingFormDraft();
                navigate('/bookings/new', {
                  replace: true,
                  state: { clientId: createdClientId },
                });
              }}
            >
              {t('Запланировать тренировку')}
            </button>
            <button
              type="button"
              className="btn-secondary w-full min-h-12"
              onClick={() =>
                navigate(`/clients/${createdClientId}/plans/new`, { replace: true })
              }
            >
              {t('Создать план тренировок')}
            </button>
          </div>
        )}

        <button
          type="button"
          className="btn-secondary w-full mt-4 min-h-12"
          onClick={() => (createdClientId ? openCreatedClient(createdClientId) : close())}
        >
          {t('Готово')}
        </button>
      </FormPageLayout>
    );
  }

  return (
    <FormPageLayout
      title={t('Пригласить клиента modal')}
      onBack={close}
      maxWidthClassName="max-w-md"
    >
      <form onSubmit={handleInvite} className="space-y-5">
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-600 rounded-2xl text-center text-sm font-medium">
            {error}
          </div>
        )}

        <div className="space-y-1.5">
          <label className="text-sm font-semibold text-slate-800 ml-1">
            {t('Имя клиента')}
          </label>
          <input
            type="text"
            className="input-field"
            value={inviteName}
            onChange={(e) => setInviteName(e.target.value)}
            placeholder="Иван Петров"
            required
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-semibold text-slate-800 ml-1">
            {t('Логин')}
          </label>
          <input
            type="text"
            className="input-field"
            value={inviteLogin}
            onChange={(e) => setInviteLogin(e.target.value)}
            placeholder="ivan_petrov"
            autoCapitalize="off"
            autoCorrect="off"
            required
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-semibold text-slate-800 ml-1">
            {t('Электронная почта')}{' '}
            <span className="text-slate-400 font-normal">
              ({t('необязательно')})
            </span>
          </label>
          <input
            type="email"
            className="input-field"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="john@example.com"
          />
        </div>

        <p className="text-xs text-slate-500">
          {t('Пароль будет сгенерирован автоматически')}
        </p>

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
          <button type="button" className="btn-secondary flex-1 min-h-12" onClick={close}>
            {t('Отмена')}
          </button>
          <button
            type="submit"
            className="btn-primary flex-1 sm:flex-[2] min-h-12"
            disabled={isLoading}
          >
            {isLoading ? t('Создание...') : t('Создать клиента')}
          </button>
        </div>
      </form>
    </FormPageLayout>
  );
};

export default InviteClientRoute;
